/**
 * Typed error hierarchy for the terminal DSL engine.
 *
 * Engine consumers should not need to import concrete classes; the
 * engine converts any thrown `DslError` (or unknown error) into a
 * `DslErrorPayload` before returning.
 */

import type { DslErrorPayload } from "./types";

export type DslErrorCode =
  | "PARSE_ERROR"
  | "UNKNOWN_FUNCTION"
  | "ARGUMENT_ERROR"
  | "RUNTIME_ERROR";

export abstract class DslError extends Error {
  abstract readonly code: DslErrorCode;
  readonly offset?: number;
  readonly detail?: Record<string, unknown>;

  constructor(
    message: string,
    options?: { offset?: number; detail?: Record<string, unknown> }
  ) {
    super(message);
    this.name = new.target.name;
    this.offset = options?.offset;
    this.detail = options?.detail;
  }
}

export class ParseError extends DslError {
  readonly code = "PARSE_ERROR" as const;

  constructor(message: string, offset: number) {
    super(message, { offset });
  }
}

export class UnknownFunctionError extends DslError {
  readonly code = "UNKNOWN_FUNCTION" as const;

  constructor(path: string) {
    super(`Unknown function: ${path}`, { detail: { path } });
  }
}

export class ArgumentError extends DslError {
  readonly code = "ARGUMENT_ERROR" as const;

  constructor(message: string, detail?: Record<string, unknown>) {
    super(message, { detail });
  }
}

export class RuntimeError extends DslError {
  readonly code = "RUNTIME_ERROR" as const;

  constructor(message: string, detail?: Record<string, unknown>) {
    super(message, { detail });
  }
}

export function toErrorPayload(err: unknown): DslErrorPayload {
  if (err instanceof DslError) {
    return {
      code: err.code,
      message: err.message,
      offset: err.offset,
      detail: err.detail,
    };
  }
  if (err instanceof Error) {
    return { code: "RUNTIME_ERROR", message: err.message };
  }
  return { code: "RUNTIME_ERROR", message: String(err) };
}
